<template>
  <div class="ant-layout-content">
    <div class="admin-list">
      <div class="admin-list-header">
        <a-page-header class="admin-list-page-header" title="Admin Management" />
        <div class="admin-list-actions">
          <a-button type="primary" @click="showCreateModal = true" icon="plus">
            Add Admin
          </a-button>
        </div>
      </div>

      <div class="admin-list-content">
        <a-card>
          <a-table
            :columns="columns"
            :data-source="admins"
            :loading="loading"
            :pagination="pagination"
            @change="handleTableChange"
            row-key="id"
          >
            <template #admin_type="text, record">
              <a-tag :color="record.admin_type === 'superadmin' ? 'gold' : 'blue'">
                {{ record.admin_type === 'superadmin' ? 'Super Admin' : 'Admin' }}
              </a-tag>
            </template>
            
            <template #created_at="text">
              {{ formatDate(text) }}
            </template>

            <template #actions="text, record">
              <a-space>
                <a-button type="link" @click="editAdmin(record)" icon="edit">
                  Edit
                </a-button>
                <a-popconfirm
                  title="Are you sure you want to delete this admin?"
                  @confirm="deleteAdmin(record.id)"
                  ok-text="Yes"
                  cancel-text="No"
                >
                  <a-button type="link" danger icon="delete">
                    Delete
                  </a-button>
                </a-popconfirm>
              </a-space>
            </template>
          </a-table>
        </a-card>
      </div>
    </div>

    <!-- Create/Edit Admin Modal -->
    <a-modal
      :title="editingAdmin ? 'Edit Admin' : 'Create Admin'"
      :visible="showCreateModal || showEditModal"
      @ok="handleSubmit"
      @cancel="handleCancel"
      :confirm-loading="submitting"
      width="600px"
    >
      <a-form :form="form" layout="vertical">
        <a-form-item label="Full Name">
          <a-input
            v-decorator="[
              'full_name',
              { rules: [{ required: true, message: 'Please enter full name' }] }
            ]"
            placeholder="Enter full name"
          />
        </a-form-item>

        <a-form-item label="Email">
          <a-input
            v-decorator="[
              'email',
              { rules: [
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' }
              ] }
            ]"
            placeholder="Enter email address"
          />
        </a-form-item>

        <a-form-item label="Phone Number">
          <a-input
            v-decorator="['phone_number']"
            placeholder="Enter phone number"
          />
        </a-form-item>

        <a-form-item label="Address">
          <a-input
            v-decorator="['address']"
            placeholder="Enter address"
          />
        </a-form-item>

        <a-form-item label="Admin Type">
          <a-select
            v-decorator="[
              'admin_type',
              { rules: [{ required: true, message: 'Please select admin type' }] }
            ]"
            placeholder="Select admin type"
          >
            <a-select-option value="admin">Admin</a-select-option>
            <a-select-option value="superadmin">Super Admin</a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="Password" v-if="!editingAdmin">
          <a-input-password
            v-decorator="[
              'password',
              { rules: [{ required: !editingAdmin, message: 'Please enter password' }] }
            ]"
            placeholder="Enter password"
          />
        </a-form-item>

        <a-form-item label="New Password" v-if="editingAdmin">
          <a-input-password
            v-decorator="['password']"
            placeholder="Enter new password (leave blank to keep current)"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import adminAPI from '@/api/admin';

export default {
  name: 'AdminsList',
  data() {
    return {
      admins: [],
      loading: false,
      submitting: false,
      showCreateModal: false,
      showEditModal: false,
      editingAdmin: null,
      form: this.$form.createForm(this),
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} admins`,
      },
      columns: [
        {
          title: 'Full Name',
          dataIndex: 'full_name',
          key: 'full_name',
          sorter: true,
        },
        {
          title: 'Email',
          dataIndex: 'email',
          key: 'email',
          sorter: true,
        },
        {
          title: 'Phone',
          dataIndex: 'phone_number',
          key: 'phone_number',
        },
        {
          title: 'Admin Type',
          dataIndex: 'admin_type',
          key: 'admin_type',
          scopedSlots: { customRender: 'admin_type' },
          sorter: true,
        },
        {
          title: 'Created At',
          dataIndex: 'created_at',
          key: 'created_at',
          scopedSlots: { customRender: 'created_at' },
          sorter: true,
        },
        {
          title: 'Actions',
          key: 'actions',
          scopedSlots: { customRender: 'actions' },
          width: 150,
        },
      ],
    };
  },
  mounted() {
    this.fetchAdmins();
  },
  methods: {
    async fetchAdmins() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.current,
          page_size: this.pagination.pageSize,
        };
        
        const response = await adminAPI.getAdmins(params);
        this.admins = response.data.data;
        this.pagination.total = response.data.pagination.total;
      } catch (error) {
        console.error('Error fetching admins:', error);
        this.$message.error('Failed to fetch admins');
      } finally {
        this.loading = false;
      }
    },

    handleTableChange(pagination, filters, sorter) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.fetchAdmins();
    },

    editAdmin(admin) {
      this.editingAdmin = admin;
      this.showEditModal = true;
      this.$nextTick(() => {
        this.form.setFieldsValue({
          full_name: admin.full_name,
          email: admin.email,
          phone_number: admin.phone_number,
          address: admin.address,
          admin_type: admin.admin_type,
        });
      });
    },

    async handleSubmit() {
      this.form.validateFields(async (err, values) => {
        if (err) return;

        this.submitting = true;
        try {
          if (this.editingAdmin) {
            // Update admin
            await adminAPI.updateAdmin(this.editingAdmin.id, values);
            this.$message.success('Admin updated successfully');
          } else {
            // Create admin
            await adminAPI.createAdmin(values);
            this.$message.success('Admin created successfully');
          }
          
          this.handleCancel();
          this.fetchAdmins();
        } catch (error) {
          console.error('Error saving admin:', error);
          this.$message.error(error.response?.data?.error || 'Failed to save admin');
        } finally {
          this.submitting = false;
        }
      });
    },

    handleCancel() {
      this.showCreateModal = false;
      this.showEditModal = false;
      this.editingAdmin = null;
      this.form.resetFields();
    },

    async deleteAdmin(adminId) {
      try {
        await adminAPI.deleteAdmin(adminId);
        this.$message.success('Admin deleted successfully');
        this.fetchAdmins();
      } catch (error) {
        console.error('Error deleting admin:', error);
        this.$message.error(error.response?.data?.error || 'Failed to delete admin');
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.admin-list {
  padding: 24px;

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .admin-list-page-header {
      padding: 0;
    }
  }

  &-content {
    .ant-card {
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }
}

.ant-table {
  .ant-tag {
    border-radius: 12px;
    font-weight: 500;
  }
}
</style>
